// src/components/stories/StoryViewer.tsx
//
// Full-screen story viewer: per-slide progress bars, auto-advance, tap/keyboard/
// hold-to-pause navigation, swipe-down to close, RTL-mirrored controls, and
// next-slide media preloading.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, GraduationCap, UserCheck, Handshake, Mail, Calendar, Trophy, Newspaper, Building2, Flame } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Story, StoryIconName } from '@/data/stories/types';
import { StorySlide } from './StorySlide';
import { markStorySeen } from '@/hooks/useStorySeen';

const ICONS: Record<StoryIconName, typeof GraduationCap> = {
  graduation: GraduationCap,
  mentor: UserCheck,
  handshake: Handshake,
  mail: Mail,
  calendar: Calendar,
  trophy: Trophy,
  newspaper: Newspaper,
  building: Building2,
  candle: Flame,
};

interface StoryViewerProps {
  stories: Story[];
  startIndex: number;
  isRTL: boolean;
  onClose: () => void;
}

export function StoryViewer({ stories, startIndex, isRTL, onClose }: StoryViewerProps) {
  const navigate = useNavigate();
  const [storyIdx, setStoryIdx] = useState(startIndex);
  const [slideIdx, setSlideIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const story = stories[storyIdx];
  const slide = story?.slides[slideIdx];
  const containerRef = useRef<HTMLDivElement>(null);
  const press = useRef<{ x: number; y: number; t: number; held: boolean } | null>(null);
  const holdTimer = useRef<number | null>(null);

  // --- navigation ---------------------------------------------------------
  const goStory = useCallback((i: number) => {
    setStoryIdx(i);
    setSlideIdx(0);
  }, []);

  const next = useCallback(() => {
    if (!story) return;
    if (slideIdx < story.slides.length - 1) setSlideIdx((s) => s + 1);
    else if (storyIdx < stories.length - 1) goStory(storyIdx + 1);
    else onClose();
  }, [story, slideIdx, storyIdx, stories.length, goStory, onClose]);

  const prev = useCallback(() => {
    if (slideIdx > 0) setSlideIdx((s) => s - 1);
    else if (storyIdx > 0) {
      const pi = storyIdx - 1;
      setStoryIdx(pi);
      setSlideIdx(stories[pi].slides.length - 1);
    } else setSlideIdx(0);
  }, [slideIdx, storyIdx, stories]);

  // --- mark seen on entering a story --------------------------------------
  useEffect(() => {
    if (story) markStorySeen(story.id);
  }, [story]);

  // --- lock body scroll ---------------------------------------------------
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  // --- keyboard -----------------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === ' ') {
        e.preventDefault();
        setPaused((p) => !p);
      } else if (e.key === 'ArrowRight') (isRTL ? prev : next)();
      else if (e.key === 'ArrowLeft') (isRTL ? next : prev)();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isRTL, next, prev, onClose]);

  // --- preload next slide media ------------------------------------------
  useEffect(() => {
    const nextSlide = story?.slides[slideIdx + 1] ?? stories[storyIdx + 1]?.slides[0];
    nextSlide?.media.forEach((m) => {
      if (m.type === 'image') {
        const img = new Image();
        img.src = m.url;
      }
    });
  }, [story, slideIdx, storyIdx, stories]);

  // --- pointer: tap zones, hold-to-pause, swipe-down ----------------------
  const clearHold = () => {
    if (holdTimer.current) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    press.current = { x: e.clientX, y: e.clientY, t: Date.now(), held: false };
    holdTimer.current = window.setTimeout(() => {
      if (press.current) {
        press.current.held = true;
        setPaused(true);
      }
    }, 180);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    clearHold();
    const p = press.current;
    press.current = null;
    if (!p) return;

    // swipe down → close
    if (e.clientY - p.y > 90 && Math.abs(e.clientX - p.x) < 80) {
      onClose();
      return;
    }
    if (p.held) {
      setPaused(false);
      return;
    }
    // quick tap → navigate by zone
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rel = (e.clientX - rect.left) / rect.width;
    const leftZone = rel < 0.33;
    const goPrev = isRTL ? !leftZone : leftZone;
    (goPrev ? prev : next)();
  };

  const onPointerCancel = () => {
    clearHold();
    if (press.current?.held) setPaused(false);
    press.current = null;
  };

  if (!story || !slide) return null;

  const lang = isRTL ? 'ar' : 'en';
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={story.label[lang]}
    >
      {/* desktop side controls */}
      <button
        type="button"
        onClick={prev}
        aria-label="Previous"
        className="absolute start-4 top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 md:block"
      >
        <PrevIcon className="h-6 w-6" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="Next"
        className="absolute end-4 top-1/2 z-20 hidden -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 md:block"
      >
        <NextIcon className="h-6 w-6" />
      </button>

      <motion.div
        initial={{ scale: 0.92, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        className="relative aspect-[9/16] h-full max-h-[100dvh] w-full overflow-hidden bg-[#070138] sm:h-[92vh] sm:max-w-[440px] sm:rounded-2xl"
      >
        {/* slide media + text (keyed → clean restart of text reveal) */}
        <StorySlide key={slide.id} slide={slide} isRTL={isRTL} paused={paused} onCta={(url) => { onClose(); navigate(url); }} />

        {/* progress bars */}
        <div className="absolute inset-x-0 top-0 z-20 flex gap-1 p-3">
          {story.slides.map((s, i) => (
            <div key={s.id} className="h-1 flex-1 overflow-hidden rounded-full bg-white/30">
              <div
                className={cn('h-full rounded-full bg-white', i === slideIdx && 'story-progress-fill')}
                style={
                  i < slideIdx
                    ? { width: '100%' }
                    : i === slideIdx
                      ? { animationDuration: `${slide.duration}ms`, animationPlayState: paused ? 'paused' : 'running' }
                      : { width: '0%' }
                }
                onAnimationEnd={i === slideIdx ? (e) => { if (e.animationName === 'story-progress') next(); } : undefined}
              />
            </div>
          ))}
        </div>

        {/* header */}
        <div className="absolute inset-x-0 top-6 z-20 flex items-center gap-3 p-3">
          <StoryAvatar story={story} />
          <div className={cn('min-w-0 flex-1', isRTL ? 'text-right' : 'text-left')}>
            <div className="truncate text-sm font-bold text-white drop-shadow">{story.label[lang]}</div>
            {!story.pinned && !story.hideHeaderDate && (
              <div className="text-xs text-white/70">
                {new Date(story.publishedAt).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* tap / hold / swipe surface (kept below header & CTA via inset) */}
        <div
          ref={containerRef}
          className="absolute inset-0 z-10"
          style={{ top: 64, bottom: 96 }}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
        />
      </motion.div>
    </motion.div>,
    document.body,
  );
}

function StoryAvatar({ story }: { story: Story }) {
  const ring = 'ring-2 ring-white/70';
  if (story.cover.type === 'image') {
    return <img src={story.cover.value} alt="" className={cn('h-9 w-9 rounded-full object-cover', ring)} />;
  }
  if (story.cover.type === 'logo') {
    return (
      <div className={cn('flex h-9 w-9 items-center justify-center rounded-full bg-white', ring)}>
        <img src={story.cover.value} alt="" className="h-6 w-6 object-contain" />
      </div>
    );
  }
  const Icon = ICONS[story.cover.value];
  return (
    <div className={cn('flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent', ring)}>
      <Icon className="h-5 w-5 text-primary-foreground" />
    </div>
  );
}
